import React, { useEffect, useState, useMemo, useCallback } from "react";
import Swal from "sweetalert2";
import { getPendingFinanceRequests, markAsOrdered } from "../api/finance";
import api from "../api/api";
import "../styles/FinancePage.css";

// ============================================================================
// HELPERS
// ============================================================================
const addWorkingDays = (startDate, days) => {
  const result = new Date(startDate);
  let addedDays = 0;
  while (addedDays < days) {
    result.setDate(result.getDate() + 1);
    const day = result.getDay();
    if (day !== 0 && day !== 6) addedDays++;
  }
  return result;
};

const formatDate = (date) => {
  if (!date) return "—";
  const d = new Date(date);
  return d.toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" });
};

const formatRelativeDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
};

const getInitials = (name) => {
  if (!name) return "?";
  return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
};

// ============================================================================
// COMPONENT: Stat Card
// ============================================================================
const StatCard = ({ value, label, accent, delay }) => (
  <div className="stat-card" style={{ animationDelay: `${delay}ms` }}>
    <div className="stat-top">
      <span className="stat-number">{value}</span>
      <div className="stat-indicator" style={{ backgroundColor: accent }} />
    </div>
    <span className="stat-label">{label}</span>  {/* FIXED: was {span> */}
  </div>
);
// ============================================================================
// COMPONENT: Comment Item
// ============================================================================
const CommentItem = ({ comment }) => (
  <div className="comment-item">
    <div className="comment-header">
      <span className="comment-author">{comment.approver}</span>
      <span className="comment-status">{comment.status}</span>
    </div>
    <p className="comment-text">{comment.comment}</p>
    <span className="comment-date">{comment.date}</span>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const FinancePage = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedLots, setExpandedLots] = useState({});
  const [selectedItems, setSelectedItems] = useState({});
  const [sortBy, setSortBy] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);

  // Fetch finance data
  const fetchFinanceData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPendingFinanceRequests();
      // FIXED: Removed frontend status filter — backend now returns correct items
      // The backend filters for status IN ['APPROVED', 'FINANCE'] and excludes terminal FinanceApproval records
      const approvedItems = (res || [])
        .map((r) => {
          const baseDate = r.ordered_at || r.created_at;
          let deliveryDate = null;
          if (r.lead_time_days && baseDate) {
            const calculated = addWorkingDays(baseDate, r.lead_time_days);
            deliveryDate = formatDate(calculated);
          }
          return { ...r, delivery_date: deliveryDate };
        });
      setPending(approvedItems || []);
    } catch (err) {
      Swal.fire("Error", "Failed to fetch finance data", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFinanceData();
  }, [fetchFinanceData]);

  // Stats
  const stats = useMemo(() => {
    const total = pending.length;
    const totalQty = pending.reduce((sum, r) => sum + (r.quantity || 0), 0);
    const uniqueLots = new Set(pending.map(r => r.lot_number)).size;
    const withComments = pending.filter(r => r.approval_comments?.length > 0).length;
    return { total, totalQty, uniqueLots, withComments };
  }, [pending]);

  // Filter & Sort
  const filtered = useMemo(() => {
    let data = [...pending];
    const q = search.trim().toLowerCase();
    if (q) {
      data = data.filter(r =>
        (r.item?.toLowerCase() || "").includes(q) ||
        (r.requester_name?.toLowerCase() || "").includes(q) ||
        (r.lot_number?.toLowerCase() || "").includes(q) ||
        (r.cost_centre?.toLowerCase() || "").includes(q) ||
        (r.description?.toLowerCase() || "").includes(q)
      );
    }
    data.sort((a, b) => {
      switch (sortBy) {
        case "newest": return new Date(b.created_at) - new Date(a.created_at);
        case "oldest": return new Date(a.created_at) - new Date(b.created_at);
        case "item_asc": return (a.item || "").localeCompare(b.item || "");
        case "item_desc": return (b.item || "").localeCompare(a.item || "");
        case "qty_desc": return (b.quantity || 0) - (a.quantity || 0);
        case "qty_asc": return (a.quantity || 0) - (b.quantity || 0);
        case "lot_asc": return (a.lot_number || "").localeCompare(b.lot_number || "");
        default: return 0;
      }
    });
    return data;
  }, [pending, search, sortBy]);

  const grouped = useMemo(() => {
    return filtered.reduce((acc, req) => {
      const lot = req.lot_number || "Unassigned";
      if (!acc[lot]) acc[lot] = [];
      acc[lot].push(req);
      return acc;
    }, {});
  }, [filtered]);

  const lotKeys = Object.keys(grouped);

  // Handlers
  const toggleLotExpand = useCallback((lot) => {
    setExpandedLots(prev => ({ ...prev, [lot]: !prev[lot] }));
  }, []);

  const toggleSelectItem = useCallback((lot, id) => {
    setSelectedItems(prev => {
      const lotSelections = prev[lot] || [];
      return {
        ...prev,
        [lot]: lotSelections.includes(id)
          ? lotSelections.filter(itemId => itemId !== id)
          : [...lotSelections, id],
      };
    });
  }, []);

  const toggleSelectAllInLot = useCallback((lot, items) => {
    setSelectedItems(prev => {
      const allIds = items.map(i => i.id);
      const allSelected = (prev[lot] || []).length === allIds.length && allIds.length > 0;
      return { ...prev, [lot]: allSelected ? [] : allIds };
    });
  }, []);

  // ── Prompt for not-available reason ──────────────────────────────────────
  const promptNotAvailableReason = async (count = 1) => {
    const result = await Swal.fire({
      title: "Mark as Not Available in Market",
      html: `
        <p style="margin-bottom:12px;color:#6b7280;font-size:14px;">
          ${count > 1 ? `${count} items` : "This item"} will be flagged as unavailable.
          Requesters will be notified automatically.
        </p>
        <textarea id="swal-reason" class="swal2-textarea" placeholder="Reason (optional)" rows="3"
          style="resize:none;font-size:14px;"></textarea>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Mark Not Available",
      confirmButtonColor: "#e53e3e",
      cancelButtonText: "Cancel",
      cancelButtonColor: "#9ca3af",
      preConfirm: () => document.getElementById("swal-reason")?.value?.trim() || "",
    });
    return result;
  };

  // ── Single: Mark as Ordered ───────────────────────────────────────────────
  const handleMarkAsOrdered = async (id) => {
    const confirm = await Swal.fire({
      title: "Mark as Ordered?",
      text: "This item will be marked as ordered and moved to delivery tracking.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Mark Ordered",
      confirmButtonColor: "#a9ae50",
      cancelButtonText: "Cancel",
      cancelButtonColor: "#9ca3af",
    });
    if (!confirm.isConfirmed) return;

    try {
      setProcessing(true);
      await markAsOrdered(id);
      Swal.fire({ icon: "success", title: "Marked as Ordered", timer: 1800, timerProgressBar: true, confirmButtonColor: "#a9ae50" });
      fetchFinanceData();
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.detail || "Failed to mark as ordered", "error");
    } finally {
      setProcessing(false);
    }
  };

  // ── Single: Mark as Not Available ────────────────────────────────────────
  const handleMarkNotAvailable = async (id) => {
    const result = await promptNotAvailableReason(1);
    if (!result.isConfirmed) return;

    try {
      setProcessing(true);
      await api.post(`/finance-approvals/${id}/mark-not-available/`, { reason: result.value || "" });
      Swal.fire({ icon: "success", title: "Marked as Not Available", timer: 1800, timerProgressBar: true, confirmButtonColor: "#e53e3e" });
      fetchFinanceData();
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.detail || "Failed to mark as not available", "error");
    } finally {
      setProcessing(false);
    }
  };

  // ── Bulk: Mark as Ordered ─────────────────────────────────────────────────
  const handleBulkMarkAsOrdered = async (lotNumber) => {
    const selectedIds = selectedItems[lotNumber] || [];
    if (!selectedIds.length) {
      return Swal.fire("No Selection", "Please select at least one item.", "warning");
    }

    const confirm = await Swal.fire({
      title: `Mark ${selectedIds.length} items as Ordered?`,
      text: "Selected items will be marked as ordered.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Mark Ordered",
      confirmButtonColor: "#a9ae50",
      cancelButtonText: "Cancel",
      cancelButtonColor: "#9ca3af",
    });
    if (!confirm.isConfirmed) return;

    try {
      setProcessing(true);
      await api.patch("/finance-approvals/bulk/", { ids: selectedIds });
      Swal.fire({ icon: "success", title: "Marked as Ordered", text: `${selectedIds.length} items updated.`, timer: 1800, timerProgressBar: true, confirmButtonColor: "#a9ae50" });
      fetchFinanceData();
      setSelectedItems(prev => ({ ...prev, [lotNumber]: [] }));
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.detail || "Failed to process items", "error");
    } finally {
      setProcessing(false);
    }
  };

  // ── Bulk: Mark as Not Available ───────────────────────────────────────────
  const handleBulkMarkNotAvailable = async (lotNumber) => {
    const selectedIds = selectedItems[lotNumber] || [];
    if (!selectedIds.length) {
      return Swal.fire("No Selection", "Please select at least one item.", "warning");
    }

    const result = await promptNotAvailableReason(selectedIds.length);
    if (!result.isConfirmed) return;

    try {
      setProcessing(true);
      await api.patch("/finance-approvals/bulk-not-available/", {
        ids: selectedIds,
        reason: result.value || "",
      });
      Swal.fire({
        icon: "success",
        title: "Marked as Not Available",
        text: `${selectedIds.length} items flagged.`,
        timer: 1800,
        timerProgressBar: true,
        confirmButtonColor: "#e53e3e",
      });
      fetchFinanceData();
      setSelectedItems(prev => ({ ...prev, [lotNumber]: [] }));
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.detail || "Failed to mark items as not available", "error");
    } finally {
      setProcessing(false);
    }
  };

  const activeFilterCount = sortBy !== "newest" ? 1 : 0;

  // ==========================================================================
  // RENDER
  // ==========================================================================
  return (
    <div className="finance-page">
      {/* ===== HEADER ===== */}
      <header className="page-header">
        <div className="header-brand">
          <div className="brand-icon">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <rect width="32" height="32" rx="8" fill="#a9ae50" />
              <path d="M10 16L14 20L22 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="brand-text">
            <h1>Finance Dashboard</h1>
            <span>Approved Purchase Requests — Ready to Order</span>
          </div>
        </div>
      </header>

      {/* ===== STATS ===== */}
      <div className="stats-strip">
        <StatCard value={stats.total} label="Approved Items" accent="#a9ae50" delay={0} />
        <StatCard value={stats.totalQty} label="Total Quantity" accent="#0ea5e9" delay={80} />
        <StatCard value={stats.uniqueLots} label="Active Lots" accent="#a92f6f" delay={160} />
        <StatCard value={stats.withComments} label="With Comments" accent="#6b7280" delay={240} />
      </div>

      {/* ===== TOOLBAR ===== */}
      <div className="toolbar">
        <div className="search-box">
          <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Search by item, requester, lot, cost centre..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && <button className="search-x" onClick={() => setSearch("")}>×</button>}
        </div>

        <div className="toolbar-right">
          <button className={`filter-trigger ${showFilters ? "open" : ""} ${activeFilterCount > 0 ? "has-filters" : ""}`} onClick={() => setShowFilters(!showFilters)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
            <span>Sort</span>
            {activeFilterCount > 0 && <span className="filter-dot">{activeFilterCount}</span>}
          </button>
        </div>
      </div>

      {/* ===== SORT PANEL ===== */}
      {showFilters && (
        <div className="filter-sheet">
          <div className="sheet-block">
            <label>Sort Order</label>
            <div className="chip-row">
              {[
                { value: "newest", label: "Newest" },
                { value: "oldest", label: "Oldest" },
                { value: "item_asc", label: "Item A-Z" },
                { value: "item_desc", label: "Item Z-A" },
                { value: "qty_desc", label: "Qty High-Low" },
                { value: "qty_asc", label: "Qty Low-High" },
                { value: "lot_asc", label: "Lot Number" },
              ].map(opt => (
                <button key={opt.value} className={sortBy === opt.value ? "chip active" : "chip"} onClick={() => setSortBy(opt.value)}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ===== RESULTS ===== */}
      <div className="results-line">
        <span><strong>{filtered.length}</strong> approved items ready to order</span>
      </div>

      {/* ===== CONTENT ===== */}
      {loading ? (
        <div className="loading-wrap">
          <div className="spinner-dual">
            <div className="spin-ring" />
            <div className="spin-ring delay" />
          </div>
          <p>Loading finance data...</p>
        </div>
      ) : (
        <div className="content-wrap">
          {lotKeys.length === 0 ? (
            <div className="empty-wrap">
              <div className="empty-graphic">
                <svg width="100" height="100" viewBox="0 0 100 100" fill="none">
                  <circle cx="50" cy="50" r="40" fill="#f3f4f6" />
                  <circle cx="50" cy="50" r="28" stroke="#e5e7eb" strokeWidth="2" strokeDasharray="4 4" />
                  <text x="50" y="58" textAnchor="middle" fontSize="32">✓</text>
                </svg>
              </div>
              <h3>All Caught Up</h3>
              <p>No approved finance requests pending order.</p>
              {search && <button className="empty-reset" onClick={() => setSearch("")}>Clear Search</button>}
            </div>
          ) : (
            <div className="lot-stack">
              {lotKeys.map((lot) => {
                const items = grouped[lot];
                const lotSelectedIds = selectedItems[lot] || [];
                const allSelected = items.length > 0 && lotSelectedIds.length === items.length;
                const someSelected = lotSelectedIds.length > 0 && !allSelected;
                const isExpanded = expandedLots[lot] || false;

                const totalQty = items.reduce((sum, r) => sum + (r.quantity || 0), 0);
                const uniqueReqs = [...new Set(items.map(r => r.requester_name))];

                return (
                  <div key={lot} className={`lot-panel ${isExpanded ? "open" : ""}`}>
                    <div className="lot-header" onClick={() => toggleLotExpand(lot)}>
                      <div className="lot-left">
                        <div className="lot-chevron">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                            style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>
                        <div className="lot-badge">{lot}</div>
                        <div className="lot-summary">
                          <span>{items.length} item{items.length !== 1 ? "s" : ""}</span>
                          <span className="sep">•</span>
                          <span>Qty {totalQty}</span>
                          <span className="sep">•</span>
                          <span>{uniqueReqs.length} requester{uniqueReqs.length !== 1 ? "s" : ""}</span>
                        </div>
                      </div>

                      <div className="lot-right">
                        <label className="lot-select" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={allSelected}
                            ref={(el) => { if (el) el.indeterminate = someSelected; }}
                            onChange={() => toggleSelectAllInLot(lot, items)}
                          />
                          <span>{lotSelectedIds.length > 0 ? `${lotSelectedIds.length} selected` : "Select"}</span>
                        </label>
                        <div className="lot-actions">
                          <button
                            className="lot-btn order"
                            disabled={lotSelectedIds.length === 0}
                            onClick={(e) => { e.stopPropagation(); handleBulkMarkAsOrdered(lot); }}
                          >
                            Mark Ordered
                          </button>
                          <button
                            className="lot-btn not-available"
                            disabled={lotSelectedIds.length === 0}
                            onClick={(e) => { e.stopPropagation(); handleBulkMarkNotAvailable(lot); }}
                          >
                            Not Available
                          </button>
                        </div>
                        <span className="lot-num">{items.length}</span>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="lot-body">
                        <div className="item-table">
                          <div className="table-header">
                            <div className="th-check" />
                            <div className="th-item">Item</div>
                            <div className="th-desc">Description</div>
                            <div className="th-qty">Qty</div>
                            <div className="th-unit">Unit</div>
                            <div className="th-cost">Cost Centre</div>
                            <div className="th-stock">Stock</div>
                            <div className="th-remarks">Remarks</div>
                            <div className="th-requester">Requester</div>
                            <div className="th-comments">Comments</div>
                            <div className="th-delivery">Delivery</div>
                            <div className="th-action">Action</div>
                          </div>

                          {items.map((r) => {
                            const isSelected = lotSelectedIds.includes(r.id);
                            return (
                              <div key={r.id} className={`table-row ${isSelected ? "selected" : ""}`}>
                                <div className="td-check">
                                  <input type="checkbox" checked={isSelected} onChange={() => toggleSelectItem(lot, r.id)} />
                                </div>
                                <div className="td-item">
                                  <span className="td-name">{r.item}</span>
                                  <span className="td-pr">PR#{r.id}</span>
                                </div>
                                <div className="td-desc">{r.description || "—"}</div>
                                <div className="td-qty">{r.quantity}</div>
                                <div className="td-unit">{r.units || "—"}</div>
                                <div className="td-cost">{r.cost_centre || "—"}</div>
                                <div className="td-stock">{r.stock_balance ?? "—"}</div>
                                <div className="td-remarks">{r.remarks || "—"}</div>
                                <div className="td-requester">
                                  <div className="td-avatar" style={{ background: `linear-gradient(135deg, #a92f6f, #c44a8a)` }}>
                                    {getInitials(r.requester_name)}
                                  </div>
                                  <span>{r.requester_name || "—"}</span>
                                </div>
                                <div className="td-comments">
                                  {r.approval_comments?.length ? (
                                    <div className="comments-stack">
                                      {r.approval_comments.map((c, i) => (
                                        <CommentItem key={i} comment={c} />
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="no-comments">—</span>
                                  )}
                                </div>
                                <div className="td-delivery">
                                  <span className="delivery-date">{r.delivery_date || "—"}</span>
                                  {r.lead_time_days && (
                                    <span className="lead-time">{r.lead_time_days} working days lead</span>
                                  )}
                                </div>
                                <div className="td-action">
                                  <button
                                    className="act-btn order"
                                    onClick={() => handleMarkAsOrdered(r.id)}
                                    title="Mark as Ordered"
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="20 6 9 17 4 12"/>
                                    </svg>
                                  </button>
                                  <button
                                    className="act-btn not-available"
                                    onClick={() => handleMarkNotAvailable(r.id)}
                                    title="Mark as Not Available in Market"
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                      <circle cx="12" cy="12" r="10"/>
                                      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ===== PROCESSING ===== */}
      {processing && (
        <div className="process-mask">
          <div className="process-box">
            <div className="process-spin">
              <div className="spin-track" />
              <div className="spin-head" />
            </div>
            <p>Processing...</p>
            <span>Updating order status</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancePage;